// Shared Skenario typed-report UI: report renderers, generate input forms, and a
// self-contained PotensiTypeView that fetches the owned report for a (grid, type)
// or lets the user generate it (quote → confirm charge → generate → render).
// Used by both the Skenario map popup and the Deep Analysis page tabs.
import { useEffect, useState } from "react";
import { Loader2, Store, Megaphone, Users, Wallet, MapPinned, TrendingUp, Star, Clock, Building2, Target, Route, Sparkles, AlertTriangle, CheckCircle2, Layers, Download, RefreshCw, Send, Share2 } from "lucide-react";
import { getGridReports, generatePotensi, getDeepAnalysisQuote } from "../lib/api";
import ShareModal from "./ShareModal";
import { openDeepAnalysisPrint } from "../lib/printTicket";
import { ReportMap, Ecosystem, PopulationByGroup, NeighbourhoodProfile, MarketRead, Throughput, GroupHeader } from "../pages/DeepAnalysis";
import CompetitorMap from "./CompetitorMap";
import { MapLink } from "./MapLink";
import { haversineKm, radiusToKm } from "../lib/geo";

// Footer actions, mirroring the classic Deep Analysis report: provenance on the
// left, refresh + PDF on the right. Refresh re-runs a PAID generation, so it only
// opens the price confirmation — the charge itself happens after the user agrees.
// `share`: { gridId, title, ownerLabel } when the viewer may share this report (owner view);
// absent on the public shared page, so the button does not show there.
// Everything the print page (and a shared link) needs to render this report
// elsewhere. `audience` = "advertiser" is the proposal edition of an Iklan report:
// it drops the owner's ranking, pitch angles and slot pricing.
export function potensiTicket({ report, type, audience = "internal", vertical = null }) {
  const isIklan = type === "potensi_iklan";
  const region = report?.facts?.region || {};
  const r0 = (report?.ranking || [])[0];
  const v0 = (report?.verticals || [])[0];
  const gid = report?.facts?.grid_id;
  const score = isIklan ? (v0?.score ?? 0) : (r0?.opportunity ?? 0);
  const inp = report?.inputs || {};
  return {
    kind: "potensi", type, report, audience, vertical,
    view: {
      gridLabel: gid != null ? `grid-${gid}` : "Lokasi",
      fullLocation: [region.kelurahan, region.kecamatan ? `Kec. ${region.kecamatan}` : null, region.kabkot, region.provinsi].filter(Boolean).join(" · "),
      cover: {
        kicker: isIklan ? (audience === "advertiser" ? "PROPOSAL MEDIA LUAR RUANG" : "LAPORAN POTENSI IKLAN") : "LAPORAN POTENSI BISNIS",
        ring: { value: Math.round(score), max: 100, top: isIklan ? "KECOCOKAN IKLAN" : "PELUANG TERATAS", unit: "DARI 100",
                sub: isIklan ? (v0?.name || "-") : (r0?.subcategory || "-"), color: scoreColor(score) },
        metas: isIklan
          ? [["Format", inp.format || "-"], ["Posisi", inp.posisi || "-"], ["Radius", report?.radius || "-"]]
          : [["Modal", inp.modal && inp.modal !== "semua" ? inp.modal : "semua"], ["Ruang", inp.luas_m2 ? `${inp.luas_m2} m² · ${inp.tipe_ruang || ""}` : (inp.tipe_ruang || "-")], ["Radius", report?.radius || "-"]],
      },
    },
    fileName: `Landhep_grid-${gid ?? "x"}_${isIklan ? "PotensiIklan" : "PotensiBisnis"}${audience === "advertiser" ? "_Proposal" : ""}`,
  };
}

// Footer actions, mirroring the classic Deep Analysis report: provenance on the
// left, refresh + PDF on the right. Refresh re-runs a PAID generation, so it only
// opens the price confirmation — the charge itself happens after the user agrees.
// `share`: { gridId, title, ownerLabel } when the viewer may share this report (owner view);
// absent on the public shared page, so the button does not show there.
function ReportFooter({ report, type, onRefresh, share }) {
  const [shareOpen, setShareOpen] = useState(false);
  const accent = type === "potensi_bisnis" ? "#0f9d6e" : "#c026d3";
  const isIklan = type === "potensi_iklan";
  // Which category the proposal is aimed at. "" = generic location profile.
  const [target, setTarget] = useState("");
  const download = (audience) => {
    try {
      openDeepAnalysisPrint(potensiTicket({ report, type, audience, vertical: audience === "advertiser" ? target || null : null }));
    } catch (e) { alert("Gagal membuka halaman cetak: " + e.message); }
  };
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap pt-2 no-print">
      <div className="flex items-center gap-2 text-[10px] text-slate-400">
        <Building2 className="w-3 h-3" />
        Berdasarkan data sensus, OSM, Google Maps & AI. Angka bersifat perkiraan — verifikasi di lapangan sebelum memutuskan.
      </div>
      <div className="flex items-center gap-2">
        {share && (
          <>
            <button onClick={() => setShareOpen(true)}
              className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
              <Share2 className="w-3.5 h-3.5" /> Bagikan
            </button>
            <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} kind={type} gridId={share.gridId}
              title={share.title} ownerLabel={share.ownerLabel} buildPayload={() => ({ report, type })} />
          </>
        )}
        {onRefresh && (
          <button onClick={onRefresh}
            className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Perbarui Data
          </button>
        )}
        {/* The advertiser edition drops the owner's ranking, pitch angles and slot
            pricing — the things that weaken his hand if the buyer reads them. */}
        {isIklan && (report.verticals || []).length > 0 && (
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            title="Tujukan proposal ke satu kategori pengiklan"
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 cursor-pointer max-w-[190px]">
            <option value="">Proposal umum…</option>
            {/* Alphabetical, NOT by score — a dropdown ordered by rank would leak the
                ranking the advertiser edition exists to hide. */}
            {[...report.verticals].sort((a, b) => a.name.localeCompare(b.name, "id"))
              .map((v) => <option key={v.id} value={v.id}>Untuk: {v.name}</option>)}
          </select>
        )}
        {isIklan && (
          <button onClick={() => download("advertiser")}
            title="Versi untuk dikirim ke calon pengiklan — tanpa peringkat vertikal, pengiklan lokal, sudut penawaran, dan panduan harga slot"
            className="flex items-center gap-2 rounded-xl border-2 px-4 py-2.5 text-xs font-bold transition-colors"
            style={{ borderColor: accent, color: accent }}>
            <Send className="w-3.5 h-3.5" /> Proposal untuk Pengiklan
          </button>
        )}
        <button onClick={() => download("internal")}
          title={isIklan ? "Versi lengkap untuk Anda sendiri — jangan dikirim ke calon pengiklan" : undefined}
          className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold text-white transition-colors"
          style={{ backgroundColor: accent }}>
          <Download className="w-3.5 h-3.5" /> {isIklan ? "Laporan Lengkap (Internal)" : "Unduh Laporan PDF"}
        </button>
      </div>
    </div>
  );
}

const SCORE_COLORS = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#4ade80", "#15803d"];
export function scoreColor(s) {
  const cuts = [20, 40, 60, 80, 90]; let i = 0;
  while (i < cuts.length && (s ?? 0) >= cuts[i]) i++;
  return SCORE_COLORS[i];
}
const REPORT_RADIUS = "3km";

// ── Shared building blocks reused across both reports (from report.facts) ─────────
// Life stages partition the population (0–14 + 15–64 + 65+ = total), so they get a
// % share. The working-age columns are OVERLAPPING sub-bands of the productive
// band — kept separate so the two are never read as one scale.
const LIFE_STAGES = [
  ["pop_children", "Anak-anak (0–14)", "#38bdf8"],
  ["pop_productive", "Usia produktif (15–64)", "#0ea5e9"],
  ["pop_elderly", "Lansia (65+)", "#6366f1"],
];
const WORKING_AGE = [
  ["pop_youth", "Remaja"], ["pop_young_adult", "Dewasa muda"], ["pop_middle_age", "Paruh baya"],
];
const POI_LABEL = {
  office: "Kantor", government: "Pemerintah", campus: "Kampus", education: "Edukasi",
  transit: "Transit", transit_hub: "Terminal", mall: "Mal", supermarket: "Swalayan",
  convenience: "Minimarket", restaurant: "Kuliner", cafe: "Kafe", health: "Kesehatan", market: "Pasar",
};
const fmtN = (n) => (n == null ? "—" : Math.round(n).toLocaleString("id-ID"));
const radiusInt = (r) => { const n = parseInt(String(r || "3"), 10); return Number.isFinite(n) ? n : 3; };

function DemografiSection({ facts, radius }) {
  const c = facts?.catchment || {};
  const rk = radiusInt(radius);
  const cat = c[rk] || c[3] || c[5] || {};
  const pop = cat.pop_total || 0;
  if (!pop) return null;
  const female = cat.pop_female || 0;
  const fPct = pop ? Math.round(female / pop * 100) : 0;
  const pct = (v) => Math.round((v || 0) / pop * 100);
  const maxW = Math.max(1, ...WORKING_AGE.map(([k]) => cat[k] || 0));
  return (
    <Section title={`Populasi dalam radius ${rk} km`} icon={Users}>
      <p className="text-2xl font-extrabold text-slate-800 tabular-nums leading-none mb-3">{fmtN(pop)} <span className="text-[11px] font-medium text-slate-400">jiwa</span></p>
      {/* 0–14 / 15–64 / 65+ partition the population, so these three sum to the
          total and carry a % — same treatment as the classic report. */}
      <div className="space-y-1.5">
        {LIFE_STAGES.map(([k, l, color]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="w-24 text-[11px] text-slate-500 shrink-0">{l}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${Math.min(100, ((cat[k] || 0) / pop) * 100)}%`, backgroundColor: color }} />
            </div>
            <span className="w-16 text-right text-[11px] font-semibold text-slate-600 tabular-nums shrink-0">
              {fmtN(cat[k] || 0)} <span className="text-slate-400 font-normal">{pct(cat[k])}%</span>
            </span>
          </div>
        ))}
      </div>
      {/* Overlapping sub-bands of the productive band — no % of total, or it would
          read as a partition (the six columns together come to ~168% of the total). */}
      <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
        <p className="text-[10px] text-slate-400 mb-1.5">Rincian usia produktif <span className="text-slate-300">· saling beririsan</span></p>
        <div className="space-y-1.5">
          {WORKING_AGE.map(([k, l]) => <Bar key={k} label={l} value={cat[k] || 0} max={maxW} color="#94a3b8" />)}
        </div>
      </div>
      {/* Gender split */}
      <div className="mt-3">
        <div className="flex h-3 rounded-full overflow-hidden">
          <div className="bg-pink-400" style={{ width: `${fPct}%` }} title={`Perempuan ${fPct}%`} />
          <div className="bg-sky-500" style={{ width: `${100 - fPct}%` }} title={`Laki-laki ${100 - fPct}%`} />
        </div>
        <div className="flex justify-between text-[10px] mt-1">
          <span className="text-pink-500 font-semibold">♀ Perempuan {fPct}%</span>
          <span className="text-sky-600 font-semibold">Laki-laki {100 - fPct}% ♂</span>
        </div>
      </div>
    </Section>
  );
}

function DayaBeliSection({ facts, property, premium = [] }) {
  const bp = facts?.buying_power_index;
  if (bp == null && !property?.land_price_typical_idr_m2 && !premium.length) return null;
  const color = bp == null ? "#94a3b8" : bp >= 120 ? "#059669" : bp >= 90 ? "#eab308" : "#f97316";
  return (
    <Section title="Daya beli & harga" icon={Wallet}>
      <div className="flex items-center gap-4">
        {bp != null && (
          <div className="shrink-0 text-center">
            <Gauge value={Math.round(bp)} max={200} color={color} sub="/ 200" size={96} />
            <p className="text-[9px] text-slate-400 -mt-1">100 = rata-rata nasional</p>
          </div>
        )}
        <div className="min-w-0 text-[12px] text-slate-600 space-y-1">
          {bp != null && <p><b className="text-slate-800">{bp >= 120 ? "Di atas" : bp >= 90 ? "Sekitar" : "Di bawah"}</b> rata-rata nasional</p>}
          {property?.land_price_typical_idr_m2 && <p>Lahan tipikal <b className="text-slate-700">Rp {fmtN(property.land_price_typical_idr_m2)}/m²</b></p>}
          {property?.house_price_typical_idr && <p>Rumah ~<b className="text-slate-700">Rp {fmtN(property.house_price_typical_idr)}</b></p>}
          {property?.confidence && <p className="text-[10px] text-slate-400">keyakinan data: {property.confidence}</p>}
        </div>
      </div>
      {premium.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-100 dark:border-slate-700/60">
          <p className="text-[10px] text-slate-400 mb-1.5">Merek premium terdekat · sinyal kelas atas</p>
          <div className="flex flex-wrap gap-1.5">
            {premium.slice(0, 6).map((b) => (
              <span key={b.brand} className="text-[10px] rounded-full bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20 px-2 py-0.5 text-violet-700 dark:text-violet-300">
                {b.brand} <span className="text-violet-400">· {b.nearest}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {property?.listing_bias && (
        <p className="text-[10px] text-amber-600 mt-2 leading-relaxed">
          ⚠️ Harga di atas menyiratkan penghasilan pembeli yang jauh di atas kelas pendapatan kelurahan ini
          ({pretty(property.income_class)}) — pertanda listing condong ke segmen atas pasar lokal. Ini kelas pasar
          properti, bukan kondisi warganya.
        </p>
      )}
    </Section>
  );
}

function EkosistemSection({ facts }) {
  const poi = facts?.poi_context || {};
  const entries = Object.entries(poi).filter(([k]) => !k.startsWith("_") && v0(poi[k])).sort((a, b) => b[1] - a[1]).slice(0, 8);
  if (!entries.length) return null;
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <Section title="Ekosistem sekitar (1 km)" icon={Building2}>
      <div className="space-y-1.5">
        {entries.map(([k, v]) => <Bar key={k} label={POI_LABEL[k] || k} value={v} max={max} color="#8b5cf6" />)}
      </div>
    </Section>
  );
}
const v0 = (n) => (n || 0) > 0;
const pretty = (v) => (v == null || v === "" ? "—" : String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));

// #1 Area & properti — zone classification + roads + property (+ optional AI note).
function AreaPropertiSection({ facts, property, note, accent = "#0f9d6e" }) {
  const a = facts?.attrs || {}; const roads = facts?.roads || {};
  const rows = [["zone_character", "Karakter zona"], ["urban_maturity", "Kematangan urban"], ["income_level_class", "Kelas pendapatan"]].filter(([k]) => a[k]);
  if (!rows.length && !property?.land_price_typical_idr_m2 && !note) return null;
  return (
    <Section title="Analisis area & properti" icon={Building2} accent={accent} ai={!!note}>
      {note && <p className="text-[12px] leading-relaxed mb-2">{note}</p>}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
        {rows.map(([k, l]) => <div key={k} className="flex justify-between gap-2"><span className="text-slate-500">{l}</span><span className="font-semibold text-slate-700 dark:text-slate-200 text-right">{pretty(a[k])}</span></div>)}
        {roads.type_label && <div className="flex justify-between gap-2"><span className="text-slate-500">Akses jalan</span><span className="font-semibold text-slate-700 dark:text-slate-200 text-right">{pretty(roads.type_label)}</span></div>}
        {property?.land_price_typical_idr_m2 && <div className="flex justify-between gap-2"><span className="text-slate-500">Lahan/m²</span><span className="font-semibold text-slate-700 dark:text-slate-200 text-right">Rp {fmtN(property.land_price_typical_idr_m2)}</span></div>}
      </div>
    </Section>
  );
}

// #7 Profil kelurahan — curated area attributes as chips.
const PROFILE_ATTRS = [["income_level_class", "Pendapatan"], ["economic_base", "Ekonomi"], ["urban_maturity", "Urban"], ["growth_perception", "Pertumbuhan"], ["footfall_perception", "Keramaian"], ["transit_availability", "Transit"], ["dominant_anchor", "Anchor"], ["office_presence", "Kantor"]];
// Mirrors the classic report's _DENSITY_FIELDS so both tell the same story.
const KELURAHAN_DENSITY = [["fnb_density", "F&B"], ["retail_poi_density", "Ritel"], ["office_density", "Kantor"], ["education_density", "Pendidikan"], ["health_density", "Kesehatan"], ["traditional_market_density", "Pasar"], ["transit_hub_density", "Transit"], ["entertainment_density", "Hiburan"]];
function ProfilKelurahanSection({ facts, accent = "#0f9d6e" }) {
  const a = facts?.attrs || {}; const rows = PROFILE_ATTRS.filter(([k]) => a[k]);
  const dens = KELURAHAN_DENSITY.filter(([k]) => a[k] && a[k] !== "none");
  if (!rows.length && !dens.length) return null;
  const chip = (k, l, v) => (
    <span key={k} className="text-[10px] rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 px-2 py-0.5">
      <span className="text-slate-400">{l}:</span> <b className="text-slate-700 dark:text-slate-200">{pretty(v)}</b>
    </span>
  );
  return (
    <Section title="Profil kelurahan" icon={Layers} accent={accent}>
      <div className="flex flex-wrap gap-1.5">
        {rows.map(([k, l]) => chip(k, l, a[k]))}
      </div>
      {/* Kelurahan-level density bands, same set the classic report shows. Coarser
          than the Google count above — one label for the whole kelurahan — so it is
          kept in its own row rather than mixed with it. */}
      {dens.length > 0 && (
        <>
          <p className="text-[10px] text-slate-400 mt-3 mb-1.5">Kepadatan POI di kelurahan</p>
          <div className="flex flex-wrap gap-1.5">
            {dens.map(([k, l]) => chip(k, l, a[k]))}
          </div>
        </>
      )}
    </Section>
  );
}

// #4 Competitor busyness proxy (review counts).
function VolumeSection({ market, accent = "#0f9d6e" }) {
  const comps = (market?.competitors || []).filter((c) => c.reviews != null).sort((a, b) => (b.reviews || 0) - (a.reviews || 0)).slice(0, 6);
  if (!comps.length) return null;
  const max = Math.max(1, ...comps.map((c) => c.reviews || 0));
  return (
    <Section title={`Estimasi keramaian pesaing — ${market?.subcategory || "kandidat #1"}`} icon={TrendingUp} accent={accent}>
      <p className="text-[10px] text-slate-400 mb-2">Proksi dari jumlah ulasan Google — makin banyak ulasan, makin ramai.</p>
      <div className="space-y-1.5">{comps.map((c, i) => <Bar key={i} label={c.name} value={c.reviews || 0} max={max} color="#f59e0b" suffix=" ulasan" />)}</div>
    </Section>
  );
}

// #6 Area rhythm (dayparting) — reused for Bisnis (Iklan already has its own).
function RitmeSection({ items, accent = "#0f9d6e" }) {
  if (!(items || []).length) return null;
  return (
    <Section title="Ritme area vs jam operasi" icon={Clock} accent={accent} ai>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{items.map((d, i) => (
        <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-800/40 px-2.5 py-2">
          <div className="flex items-center gap-1 mb-0.5" style={{ color: accent }}><Clock className="w-3 h-3" /><span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">{d.waktu}</span></div>
          <p className="text-[10px] text-slate-500 leading-snug">{d.aktivitas}</p>
        </div>))}</div>
    </Section>
  );
}

// ── Self-contained tab body for one report type ─────────────────────────────────
// grid: { grid_id, lat, lng, region:{ kelurahan,kecamatan,kabkot,provinsi,kode_kelurahan,kode_kabkot } }
export function PotensiTypeView({ type, grid, asset }) {
  const at = asset?.attrs || {};
  const [owned, setOwned] = useState(undefined);   // undefined=loading, null=none, obj=report
  const [inputs, setInputs] = useState(type === "potensi_bisnis"
    ? { modal: "semua", luas_m2: at.luas_m2 || "", tipe_ruang: at.tipe_ruang || "ruko", rencana: "operasikan sendiri" }
    : { format: at.format || "videotron", posisi: at.posisi || "tepi jalan raya", jam_tayang: at.jam_tayang || "24jam", pembatasan_vertikal: [] });
  const [confirm, setConfirm] = useState(null);    // { quote, loading }
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);   // owned, but re-opening the form
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setOwned(undefined);
    getGridReports(grid.grid_id)
      .then((r) => { if (alive) setOwned(r?.reports?.[type]?.report || null); })
      .catch(() => { if (alive) setOwned(null); });
    return () => { alive = false; };
  }, [grid.grid_id, type]);

  // Adopt the options the owned report was actually built with, so a refresh
  // repeats those choices instead of silently reverting to the defaults — the
  // user is paying for the rerun and would have no way to see the difference.
  // Blank/absent fields keep the default rather than wiping the form.
  useEffect(() => {
    const stored = owned?.inputs;
    if (!stored) return;
    setInputs((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(stored)) {
        if (v !== null && v !== undefined && v !== "") next[k] = v;
      }
      return next;
    });
  }, [owned]);

  // While a PAID generation runs, warn before the tab is closed/reloaded so the
  // user can't navigate away and lose the (charged) report mid-flight.
  useEffect(() => {
    if (!generating) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [generating]);

  async function askGenerate() {
    setErr(null); setConfirm({ quote: null, loading: true });
    try { setConfirm({ quote: await getDeepAnalysisQuote(grid.grid_id, null, REPORT_RADIUS, type), loading: false }); }
    catch { setConfirm({ quote: null, loading: false }); }
  }
  async function doGenerate() {
    setConfirm(null); setGenerating(true); setErr(null);
    try {
      const r = await generatePotensi({
        report_type: type, grid_id: grid.grid_id, lat: grid.lat, lng: grid.lng, radius: REPORT_RADIUS, inputs,
        kode_kelurahan: grid.region?.kode_kelurahan, kode_kabkot: grid.region?.kode_kabkot,
        nama_kelurahan: grid.region?.kelurahan, nama_kecamatan: grid.region?.kecamatan,
        nama_kabkot: grid.region?.kabkot, nama_provinsi: grid.region?.provinsi,
      });
      if (r?.ok) { setOwned(r.report); setEditing(false); } else setErr(r?.error || "Gagal membuat laporan");
    } catch (e) { setErr(e.message); }
    finally { setGenerating(false); }
  }

  // Refreshing an owned report is a fresh PAID run, so it goes through the very
  // same quote → confirm → generate path as the first one; only the wording of the
  // dialog differs. Rendered from both branches, hence lifted out here.
  const isRefresh = !!owned;
  const confirmModal = confirm && (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-base font-bold text-slate-800 mb-1">{isRefresh ? "Perbarui data laporan?" : "Buat laporan berbayar?"}</h2>
          <p className="text-xs text-slate-500 leading-relaxed">
            {isRefresh
              ? "Laporan disusun ulang memakai AI + data lapangan terbaru. Kredit kamu akan dipotong sesuai tarif pembaruan di bawah, dan laporan lama digantikan."
              : "Laporan ini memakai AI + data lapangan langsung, dan menjadi milikmu."}
          </p>
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            {confirm.loading ? <div className="flex items-center gap-2 text-slate-400 text-xs"><Loader2 className="w-4 h-4 animate-spin" /> Menghitung biaya…</div>
              : confirm.quote?.billing_enabled === false ? <p className="text-[11px] text-slate-500">Penagihan nonaktif — gratis di lingkungan ini.</p>
              : confirm.quote ? <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{confirm.quote.is_first ? "Biaya laporan baru" : "Biaya pembaruan"}</span><span className="text-sm font-extrabold text-slate-800 tabular-nums">Rp {Math.round(confirm.quote.price || 0).toLocaleString("id-ID")}</span></div>
              : <p className="text-[11px] text-amber-700">Kamu akan ditagih sesuai tarif saat membuat.</p>}
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-2">
          <button onClick={() => setConfirm(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Batal</button>
          <button onClick={doGenerate} disabled={confirm.loading} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-50">{isRefresh ? "Perbarui" : "Lanjutkan"}</button>
        </div>
      </div>
    </div>
  );

  // What the Bagikan modal needs to label and verify the share.
  const reg = grid?.region || {};
  const shareCtx = grid?.grid_id ? {
    gridId: grid.grid_id,
    title: `${type === "potensi_bisnis" ? "Potensi Bisnis" : "Potensi Iklan"} · grid-${grid.grid_id}${reg.kelurahan ? ` · ${reg.kelurahan}` : ""}${reg.kabkot ? `, ${reg.kabkot}` : ""}`,
    ownerLabel: (() => { try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}").businessName || null; } catch { return null; } })(),
  } : null;
  if (generating) return <GeneratingBlocker />;
  if (owned === undefined) return <Centered><Loader2 className="w-6 h-6 animate-spin text-amber-500" /><p className="text-xs font-semibold mt-2">Memuat…</p></Centered>;
  if (owned && !editing) return (
    <>
      {type === "potensi_bisnis"
        ? <BisnisReport report={owned} onRefresh={() => { setErr(null); setEditing(true); }} share={shareCtx} />
        : <IklanReport report={owned} onRefresh={() => { setErr(null); setEditing(true); }} share={shareCtx} />}
      {confirmModal}
    </>
  );

  const accent = type === "potensi_bisnis" ? "#059669" : "#c026d3";
  const Icon = type === "potensi_bisnis" ? Store : Megaphone;
  return (
    <div className="px-6 py-5 max-w-2xl">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5" style={{ color: accent }} /><p className="text-sm font-bold text-slate-700">{type === "potensi_bisnis" ? "Potensi Bisnis" : "Potensi Iklan"}</p></div>
      {err && <p className="mb-3 text-[11px] text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
      <p className="text-[12px] text-slate-500 mb-4">
        {editing
          ? "Opsi di bawah adalah yang dipakai laporan sekarang — ubah dulu bila perlu, lalu perbarui. Biaya ditampilkan sebelum kredit dipotong."
          : "Kamu belum punya laporan ini untuk grid ini. Isi detail lalu buat laporannya."}
      </p>
      {type === "potensi_bisnis" ? <BisnisInputs value={inputs} onChange={setInputs} /> : <IklanInputs value={inputs} onChange={setInputs} />}
      <div className="mt-5 flex gap-2">
        {editing && (
          <button onClick={() => setEditing(false)}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Batal
          </button>
        )}
        <button onClick={askGenerate} className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold" style={{ backgroundColor: accent }}>
          {editing ? "Perbarui laporan" : "Buat laporan"}
        </button>
      </div>

      {confirmModal}
    </div>
  );
}

function Centered({ children }) {
  return <div className="py-20 flex flex-col items-center justify-center text-slate-400">{children}</div>;
}

// Full-screen blocker shown while a PAID report is being generated — prevents the
// user from clicking away and losing the charged run.
function GeneratingBlocker() {
  return (
    <div className="fixed inset-0 z-[300] flex flex-col items-center justify-center gap-3 px-6 text-center"
      style={{ backgroundColor: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)" }}>
      <Loader2 className="w-9 h-9 animate-spin text-white" />
      <p className="text-sm font-bold text-white">Menyusun laporan…</p>
      <p className="text-[12px] text-white/75 max-w-xs leading-relaxed">AI menganalisis data lapangan langsung — mohon jangan tutup atau pindah halaman (~20–40 detik).</p>
    </div>
  );
}

// ── Generate input forms ────────────────────────────────────────────────────────
function Field({ label, children }) {
  return <label className="block"><span className="block text-[11px] font-bold text-slate-500 mb-1">{label}</span>{children}</label>;
}
const SEL = "w-full text-[13px] border border-slate-200 rounded-lg px-2.5 py-2 outline-none focus:border-amber-400";
export function BisnisInputs({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Modal usaha">
        <select className={SEL} value={value.modal} onChange={(e) => set("modal", e.target.value)}>
          <option value="semua">Semua tingkat</option><option value="mikro">&lt; Rp 50 jt</option>
          <option value="kecil">Rp 50–200 jt</option><option value="menengah">Rp 200 jt – 1 M</option><option value="besar">&gt; Rp 1 M</option>
        </select>
      </Field>
      <Field label="Rencana pemanfaatan">
        <select className={SEL} value={value.rencana} onChange={(e) => set("rencana", e.target.value)}>
          <option value="operasikan sendiri">Operasikan sendiri</option><option value="sewakan">Sewakan ke penyewa</option><option value="waralaba">Waralaba</option>
        </select>
      </Field>
      <Field label="Luas ruang (m²)">
        <input type="number" className={SEL} value={value.luas_m2} onChange={(e) => set("luas_m2", e.target.value)} placeholder="opsional" />
      </Field>
      <Field label="Tipe ruang">
        <select className={SEL} value={value.tipe_ruang} onChange={(e) => set("tipe_ruang", e.target.value)}>
          <option value="lahan">Lahan kosong</option><option value="ruko">Ruko</option><option value="kios">Kios</option><option value="lantai dasar">Lantai dasar gedung</option>
        </select>
      </Field>
    </div>
  );
}
const AD_VERTICALS = [["rokok", "Rokok"], ["politik", "Politik"], ["fintech", "Pinjol/Fintech"], ["hiburan", "Hiburan"]];
export function IklanInputs({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const ban = value.pembatasan_vertikal || [];
  const toggleBan = (id) => set("pembatasan_vertikal", ban.includes(id) ? ban.filter((x) => x !== id) : [...ban, id]);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Format layar">
          <select className={SEL} value={value.format} onChange={(e) => set("format", e.target.value)}>
            <option value="videotron">Videotron besar</option><option value="billboard">Billboard</option><option value="led_kecil">LED kecil</option><option value="running_text">Running text</option>
          </select>
        </Field>
        <Field label="Jam tayang">
          <select className={SEL} value={value.jam_tayang} onChange={(e) => set("jam_tayang", e.target.value)}>
            <option value="24jam">24 jam</option><option value="siang">Siang saja</option><option value="custom">Custom</option>
          </select>
        </Field>
      </div>
      {/* We hold no OOH inventory — osm_poi has no advertising category and Places does
          not treat a billboard as a place — so share of voice can only be declared. */}
      <Field label="Layar OOH lain di ruas ini (opsional)">
        <input type="number" min="0" max="50" className={SEL} placeholder="mis. 3 — untuk menghitung share of voice"
          value={value.layar_pesaing ?? ""}
          onChange={(e) => set("layar_pesaing", e.target.value === "" ? "" : Math.max(0, parseInt(e.target.value, 10) || 0))} />
        <p className="text-[10px] text-slate-400 mt-1">Kami tidak punya data inventaris OOH — angka ini dari pengamatan Anda sendiri, dan dilabeli begitu di laporan.</p>
      </Field>
      <Field label="Posisi & visibilitas">
        <select className={SEL} value={value.posisi} onChange={(e) => set("posisi", e.target.value)}>
          <option value="tepi jalan raya">Tepi jalan raya</option><option value="persimpangan">Persimpangan</option><option value="indoor gedung">Indoor gedung</option><option value="area parkir">Area parkir</option>
        </select>
      </Field>
      <div>
        <span className="block text-[11px] font-bold text-slate-500 mb-1">Hindari vertikal (opsional)</span>
        <div className="flex flex-wrap gap-1.5">
          {AD_VERTICALS.map(([id, l]) => (
            <button key={id} onClick={() => toggleBan(id)}
              className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${ban.includes(id) ? "bg-red-50 border-red-200 text-red-600" : "bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"}`}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Report renderers ─────────────────────────────────────────────────────────────
function VerdictBadge({ v }) {
  const m = { "belum tergarap": ["bg-emerald-100 text-emerald-700", "Belum tergarap"], "seimbang": ["bg-slate-100 text-slate-600", "Seimbang"], "jenuh": ["bg-red-100 text-red-600", "Jenuh"] };
  const [cls, l] = m[v] || m.seimbang;
  return <span className={`inline-block rounded text-[8px] font-black uppercase tracking-wider px-1 py-0.5 ${cls}`}>{l}</span>;
}
// A section is now a standalone CARD (sits on a tinted report background).
// `ai` is accepted for callers' sake but no longer draws a badge — the group header says it once.
function Section({ title, icon: Icon, ai, accent = "#0f9d6e", children, long = false }) {  // eslint-disable-line no-unused-vars
  return (
    <div className={`da-card ${long ? "da-long" : ""} bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/60 shadow-sm px-5 py-4`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="w-2 h-2 rounded-[3px] shrink-0" style={{ backgroundColor: accent }} />
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
        <h3 className="text-[13px] font-extrabold text-slate-800 dark:text-slate-100">{title}</h3>

      </div>
      {children}
    </div>
  );
}

// Colored hero band with a big score ring + top pick.
function Hero({ from, to, eyebrow, title, sub, chip, ringValue, ringMax = 100, ringLabel = "peluang", pickEyebrow, pickName, verdict }) {
  const pct = Math.max(0, Math.min(1, (ringValue || 0) / ringMax));
  const c = 2 * Math.PI * 46;
  return (
    <div className="relative overflow-hidden rounded-2xl px-5 py-4 text-white shadow-sm" style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
      <div className="absolute -right-16 -bottom-24 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,255,255,.18), transparent 60%)" }} />
      <div className="relative flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-white/75">{eyebrow}</p>
          <p className="text-xl font-extrabold leading-tight">{title}</p>
          {sub && <p className="text-[12px] text-white/85 mt-0.5">{sub}</p>}
          {chip && <span className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-white/15 border border-white/25 px-2.5 py-1 text-[11px] font-bold">{chip}</span>}
        </div>
        {ringValue != null && (
          <div className="flex items-center gap-3 shrink-0">
            <svg width="102" height="102" viewBox="0 0 102 102">
              <circle cx="51" cy="51" r="46" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="9" />
              <circle cx="51" cy="51" r="46" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform="rotate(-90 51 51)" />
              <text x="51" y="49" textAnchor="middle" style={{ fontSize: "29px", fontWeight: 800, fill: "#fff" }}>{Math.round(ringValue)}</text>
              <text x="51" y="66" textAnchor="middle" style={{ fontSize: "9px", fill: "rgba(255,255,255,.8)" }}>{ringLabel} /{ringMax}</text>
            </svg>
            {pickName && (
              <div className="min-w-0">
                <p className="text-[9px] font-black uppercase tracking-widest text-white/70">{pickEyebrow}</p>
                <p className="text-base font-extrabold truncate max-w-[140px]">{pickName}</p>
                {verdict && <span className="inline-block mt-1 text-[9px] font-black uppercase tracking-wider bg-white/90 rounded px-2 py-0.5" style={{ color: to }}>{verdict}</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// KPI tiles — a dashboard-style header so the report reads visually, not as text.
function StatRow({ tiles }) {
  const t = (tiles || []).filter(Boolean);
  if (!t.length) return null;
  return (
    <div className="px-6 py-4 border-b border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
      {t.map((x, i) => (
        <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
          <div className="flex items-center gap-1 text-slate-400">{x.icon && <x.icon className="w-3 h-3" />}<span className="text-[9px] font-bold uppercase tracking-widest">{x.label}</span></div>
          <p className="text-lg font-extrabold tabular-nums leading-tight mt-0.5" style={{ color: x.accent || "#0f172a" }}>{x.value}</p>
          {x.sub && <p className="text-[10px] text-slate-400 truncate">{x.sub}</p>}
        </div>
      ))}
    </div>
  );
}

// Circular gauge (ring) with the value in the centre.
function Gauge({ value, max = 100, color = "#059669", sub, size = 108 }) {
  const pct = Math.max(0, Math.min(1, (value || 0) / max));
  const r = size / 2 - 9, cx = size / 2, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#e2e8f0" strokeWidth="9" />
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} transform={`rotate(-90 ${cx} ${cx})`} />
      <text x={cx} y={cx - 2} textAnchor="middle" style={{ fontSize: `${size * 0.26}px`, fontWeight: 800, fill: "#0f172a" }}>{Math.round(value)}</text>
      {sub && <text x={cx} y={cx + size * 0.16} textAnchor="middle" style={{ fontSize: "9px", fill: "#94a3b8" }}>{sub}</text>}
    </svg>
  );
}

// Radar / spider chart. items: [{label, value 0..1}].
function RadarChart({ items, color = "#c026d3", size = 230 }) {
  const n = items.length; if (!n) return null;
  const cx = size / 2, cy = size / 2, R = size / 2 - 34;
  const ang = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const pt = (i, r) => [cx + Math.cos(ang(i)) * R * r, cy + Math.sin(ang(i)) * R * r];
  const poly = (r) => items.map((_, i) => pt(i, r).join(",")).join(" ");
  const dataPoly = items.map((it, i) => pt(i, Math.max(0.03, Math.min(1, it.value))).join(",")).join(" ");
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto max-w-full">
      {[0.25, 0.5, 0.75, 1].map((g, gi) => <polygon key={gi} points={poly(g)} fill="none" stroke="#e2e8f0" strokeWidth="1" />)}
      {items.map((_, i) => { const [x, y] = pt(i, 1); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#e2e8f0" strokeWidth="1" />; })}
      <polygon points={dataPoly} fill={color + "33"} stroke={color} strokeWidth="2" />
      {items.map((it, i) => { const [x, y] = pt(i, 1.18); return <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: "8.5px", fontWeight: 600, fill: "#64748b" }}>{it.label}</text>; })}
    </svg>
  );
}

// Opportunity quadrant: x = fit (→ better), y = saturation (low at top = terbuka).
// x is spread across the actual fit range so points don't pile up; no dot labels
// (names live in the ranking list) — a tooltip shows on hover.
function Quadrant({ points }) {
  const pts = (points || []).filter((p) => p.x != null && p.y != null);
  if (!pts.length) return null;
  const W = 340, H = 240, pad = 34;
  const xs = pts.map((p) => p.x);
  let minX = Math.min(...xs), maxX = Math.max(...xs);
  if (maxX - minX < 0.02) { minX -= 0.05; maxX += 0.05; }          // avoid a flat line
  const spanX = maxX - minX;
  const maxY = Math.max(0.1, ...pts.map((p) => p.y));
  const X = (v) => pad + ((v - minX) / spanX) * (W - 2 * pad);
  const Y = (v) => pad + (v / maxY) * (H - 2 * pad);               // 0 sat at top
  const midX = pad + 0.62 * (W - 2 * pad);
  const midY = pad + 0.5 * (H - 2 * pad);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {/* sweet spot: high fit (right) + low saturation (top) */}
      <rect x={midX} y={pad} width={W - pad - midX} height={midY - pad} rx="6" fill="#ecfdf5" />
      <text x={W - pad - 4} y={pad + 12} textAnchor="end" style={{ fontSize: "9px", fontWeight: 800, fill: "#0a7d57", letterSpacing: ".05em" }}>SWEET SPOT</text>
      <line x1={midX} y1={pad} x2={midX} y2={H - pad} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
      <line x1={pad} y1={midY} x2={W - pad} y2={midY} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
      <text x={W / 2} y={H - 8} textAnchor="middle" style={{ fontSize: "9px", fill: "#94a3b8" }}>fit rendah → fit tinggi</text>
      <text x={12} y={H / 2} textAnchor="middle" transform={`rotate(-90 12 ${H / 2})`} style={{ fontSize: "9px", fill: "#94a3b8" }}>jenuh ← → terbuka</text>
      {pts.map((p, i) => (
        <circle key={i} cx={X(p.x)} cy={Y(p.y)} r="5" fill={p.color} fillOpacity="0.9" stroke="#fff" strokeWidth="1.25">
          <title>{p.label}</title>
        </circle>
      ))}
    </svg>
  );
}

// Horizontal bar row (label · bar · value).
function Bar({ label, value, max, color = "#059669", suffix = "" }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 text-[11px] text-slate-500 shrink-0">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${max ? Math.min(100, (value / max) * 100) : 0}%`, backgroundColor: color }} /></div>
      <span className="w-12 text-right text-[11px] font-semibold text-slate-600 tabular-nums shrink-0">{fmtN(value)}{suffix}</span>
    </div>
  );
}
// ── Sections new to the richer reports ───────────────────────────────────────────

const RADII = [1, 3, 5, 10];
// How the audience grows with distance — the one view the klasik report cannot
// give (it has a single catchment), from the 11-radius table Potensi carries.
function PopulasiMultiRadius({ facts, radius, accent = "#0f9d6e" }) {
  const c = facts?.catchment || {};
  const rows = RADII.filter((k) => (c[k] || {}).pop_total);
  if (rows.length < 2) return null;
  const rk = radiusInt(radius);
  const pct = (cat, k) => (cat.pop_total ? Math.round(((cat[k] || 0) / cat.pop_total) * 100) : 0);
  return (
    <Section title="Populasi per radius" icon={Users} accent={accent}>
      <p className="text-[10px] text-slate-400 mb-2">Berapa orang yang tinggal dalam tiap jarak dari titik ini (sensus). Radius laporan: {rk} km.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] tabular-nums">
          <thead><tr className="text-[9px] uppercase tracking-wide text-slate-400 text-right">
            <th className="text-left pb-1.5 font-bold">Radius</th><th className="pb-1.5 font-bold">Penduduk</th>
            <th className="pb-1.5 font-bold">Anak 0–14</th><th className="pb-1.5 font-bold">Produktif 15–64</th><th className="pb-1.5 font-bold">Lansia 65+</th><th className="pb-1.5 font-bold">Perempuan</th>
          </tr></thead>
          <tbody>
            {rows.map((k) => { const cat = c[k]; return (
              <tr key={k} className={`border-t border-slate-100 text-right ${k === rk ? "font-bold text-slate-800" : "text-slate-600"}`}>
                <td className="text-left py-1.5">{k} km{k === rk ? " ·" : ""}</td>
                <td className="py-1.5">{fmtN(cat.pop_total)}</td>
                <td className="py-1.5">{pct(cat, "pop_children")}%</td>
                <td className="py-1.5">{pct(cat, "pop_productive")}%</td>
                <td className="py-1.5">{pct(cat, "pop_elderly")}%</td>
                <td className="py-1.5">{pct(cat, "pop_female")}%</td>
              </tr>); })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// Klasik-style market read for ONE candidate business: its own competitor map,
// the competitor table, the market cards, the throughput model and what their
// customers say — the klasik report's Kompetisi group, per candidate.
function KandidatPasarSection({ market, index, bacaan, center, radiusKm, printMode, accent = "#0f9d6e" }) {
  if (!market) return null;
  const comps = (market.competitors || []).filter((c) =>
    !radiusKm || !Number.isFinite(center?.lat) || !Number.isFinite(c?.lat) || haversineKm(center.lat, center.lng, c.lat, c.lng) <= radiusKm * 1.5);
  const cnt = `${market.count ?? comps.length}${market.capped ? "+" : ""}`;
  const m = market.metrics || null;
  return (
    <Section title={`Kandidat #${index + 1} — ${market.subcategory}`} icon={Store} accent={accent} long>
      <p className="text-[11px] text-slate-500 mb-3">
        <b className="text-slate-700">{cnt} pesaing nyata</b> dalam radius{market.avg_rating ? ` · rating rata-rata ${market.avg_rating}★` : ""}{market.total_reviews ? ` · ${fmtN(market.total_reviews)} ulasan total` : ""} · via Google Maps
      </p>
      {bacaan && <p className="text-[12px] leading-relaxed mb-3 rounded-xl bg-violet-50/60 border border-violet-100 px-3.5 py-2.5"><span className="text-[9px] font-black uppercase tracking-wider text-violet-500 mr-1.5">Bacaan</span>{bacaan}</p>}
      {comps.some((c) => c.lat != null) && (
        <div className="mb-3">
          <CompetitorMap center={center} competitors={comps} numbered={8} staticImage={printMode} label="Pesaing" />
        </div>
      )}
      {comps.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-400">
              <tr><th className="px-2.5 py-2 font-bold">#</th><th className="px-2.5 py-2 font-bold">Pesaing</th><th className="px-2.5 py-2 font-bold">Harga</th><th className="px-2.5 py-2 font-bold text-right">Rating</th><th className="px-2.5 py-2 font-bold text-right">Ulasan</th><th className="px-2.5 py-2 font-bold text-right">Jarak</th><th className="px-2.5 py-2 font-bold">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {comps.slice(0, 12).map((c, i) => (
                <tr key={i}>
                  <td className="px-2.5 py-1.5 text-slate-300 font-bold">{i + 1}</td>
                  <td className="px-2.5 py-1.5 font-semibold text-slate-700"><MapLink place={c}>{c.name}</MapLink></td>
                  <td className="px-2.5 py-1.5 text-slate-500">{c.price || "—"}</td>
                  <td className="px-2.5 py-1.5 text-right text-amber-600 font-semibold">{c.rating ?? "—"}</td>
                  <td className="px-2.5 py-1.5 text-right text-slate-500 tabular-nums">{c.reviews != null ? fmtN(c.reviews) : "—"}</td>
                  <td className="px-2.5 py-1.5 text-right text-slate-400">{c.distance_label || "—"}</td>
                  <td className="px-2.5 py-1.5">{String(c.business_status || "").startsWith("CLOSED") ? <span className="text-[10px] font-bold text-red-500">tutup</span> : <span className="text-[10px] text-emerald-600">buka</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-[11px] text-slate-400">Tidak ada pesaing nyata ditemukan dalam radius ini.</p>}
      {m && (m.competitor_quality || m.saturation || m.survivability || m.price_positioning) && (
        <div className="mt-3"><MarketRead market={m} /></div>
      )}
      {m?.throughput?.per_competitor?.length > 0 && (
        <div className="mt-3 rounded-xl border border-slate-100 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Estimasi volume pesaing</p>
          <Throughput throughput={m.throughput} />
        </div>
      )}
      {market.voc && (
        <p className="mt-3 text-[12px] leading-relaxed italic text-slate-600 rounded-xl bg-slate-50 px-3.5 py-2.5"><Star className="inline w-3 h-3 text-amber-400 mr-1 -mt-0.5" />Suara pelanggan pesaing: "{market.voc}"</p>
      )}
    </Section>
  );
}

// Rent per m² (AI estimate, anchored on land price) and what that means per
// recommended business at its typical floor area.
function SewaSection({ rent, picks, accent = "#0f9d6e" }) {
  if (!rent?.per_m2_bulan_idr) return null;
  const withRent = (picks || []).filter((p) => p.sewa_bulanan);
  return (
    <Section title="Estimasi sewa" icon={Wallet} accent={accent} ai>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-extrabold text-slate-800 tabular-nums">Rp {fmtN(rent.per_m2_bulan_idr)}</span>
        <span className="text-[11px] text-slate-500">/ m² / bulan · kelas <b className="text-slate-700">{rent.kelas || "-"}</b></span>
      </div>
      {rent.dasar && <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{rent.dasar}</p>}
      {withRent.length > 0 && (
        <div className="mt-3 space-y-1">
          {withRent.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[11px] border-t border-slate-100 pt-1">
              <span className="text-slate-600 truncate">{p.bisnis} <span className="text-slate-400">· {p.sewa_bulanan.luas_m2[0]}–{p.sewa_bulanan.luas_m2[1]} m²</span></span>
              <span className="font-semibold text-slate-700 tabular-nums shrink-0">Rp {fmtN(p.sewa_bulanan.rendah_idr)}–{fmtN(p.sewa_bulanan.tinggi_idr)}/bln</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Perkiraan AI dari harga lahan & kelas area, bukan daftar sewa terukur.</p>
    </Section>
  );
}

// Who, around THIS screen, actually buys slots — real places by name, ranked.
function PengiklanLokalSection({ advertisers, ai, accent = "#c026d3" }) {
  const groups = (advertisers || []).filter((g) => g.count > 0);
  if (!groups.length) return null;
  const aiBy = {};
  (ai || []).forEach((a) => { if (a?.kelompok) aiBy[String(a.kelompok).toLowerCase()] = a; });
  const max = Math.max(1, ...groups.map((g) => g.score || 0));
  return (
    <Section title="Pengiklan lokal potensial" icon={Megaphone} accent={accent} long>
      <p className="text-[11px] text-slate-500 mb-3">Institusi dan bisnis nyata di sekitar titik yang lazim membeli slot OOH lokal, diurutkan dari yang paling layak didekati. Jumlah "20+" berarti pencarian Google mencapai batasnya.</p>
      <div className="space-y-3">
        {groups.map((g) => { const a = aiBy[g.label.toLowerCase()]; return (
          <div key={g.id} className="rounded-xl border border-slate-100 p-3.5">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13px] font-extrabold text-slate-800">{g.label}</p>
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{g.count}{g.capped ? "+" : ""} tempat</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(g.score / max) * 100}%`, backgroundColor: accent }} /></div>
                <p className="text-[10px] text-slate-400 mt-1">Biasanya mengiklankan: {g.sells}</p>
              </div>
              <span className="text-[15px] font-extrabold tabular-nums shrink-0" style={{ color: scoreColor(g.score) }}>{g.score}</span>
            </div>
            {g.places?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {g.places.map((p, i) => (
                  <span key={i} className="text-[10px] rounded-full bg-slate-50 border border-slate-100 px-2 py-0.5 text-slate-700">
                    <MapLink place={p}>{p.name}</MapLink>{p.distance_label && <span className="text-slate-400"> · {p.distance_label}</span>}{p.rating != null && <span className="text-amber-500"> · {p.rating}★</span>}
                  </span>
                ))}
              </div>
            )}
            {a && (
              <div className="mt-2.5 rounded-lg bg-violet-50/60 border border-violet-100 px-3 py-2 text-[11px] leading-relaxed">
                <span className="text-[9px] font-black uppercase tracking-wider text-violet-500 mr-1.5">Bacaan</span>
                {a.sudut_jual}
                {a.paket_saran && <p className="mt-1 text-slate-600"><b>Paket:</b> {a.paket_saran}</p>}
                {(a.prospek || []).length > 0 && <p className="mt-1 text-slate-600"><b>Prospek pertama:</b> {a.prospek.join(", ")}</p>}
              </div>
            )}
          </div>); })}
      </div>
    </Section>
  );
}

// Slot value: a transparent CPM model (assumption printed), plus the AI read.
function NilaiSlotSection({ slot, note, accent = "#c026d3" }) {
  if (!slot && !note) return null;
  return (
    <Section title="Panduan nilai slot" icon={Wallet} accent={accent} ai={!!note}>
      {slot && (
        <>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-2xl font-extrabold text-slate-800 tabular-nums">Rp {fmtN(slot.kisaran_bulanan_idr[0])} – {fmtN(slot.kisaran_bulanan_idr[1])}</span>
            <span className="text-[11px] text-slate-500">/ bulan · kelas <b className="text-slate-700">{slot.kelas}</b></span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">≈ {fmtN(slot.impresi_bulanan)} impresi/bulan × CPM Rp {fmtN(slot.cpm_idr[0])}–{fmtN(slot.cpm_idr[1])}</p>
          <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{slot.asumsi}</p>
        </>
      )}
      {note && <p className="text-[12px] leading-relaxed mt-2.5">{note}</p>}
    </Section>
  );
}

function KalenderSection({ items, accent = "#c026d3" }) {
  if (!(items || []).length) return null;
  return (
    <Section title="Kalender pengiklan lokal" icon={Clock} accent={accent} ai>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((k, i) => (
          <div key={i} className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">{k.periode}</p>
            <p className="text-[12px] font-extrabold text-slate-800">{k.momen}</p>
            {k.pengiklan && <p className="text-[10px] text-slate-500 mt-0.5">{k.pengiklan}</p>}
            {k.pesan && <p className="text-[11px] text-slate-600 mt-1 leading-snug">{k.pesan}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
}

// Area profile: the klasik component when the report carries db_facts (new
// reports), else the older chip view.
function AreaBlock({ report, accent }) {
  const dbf = report.db_facts;
  if (dbf?.area_profile?.length || dbf?.poi_density?.length) {
    return (
      <Section title="Profil kelurahan" icon={Layers} accent={accent}>
        <NeighbourhoodProfile areaProfile={dbf.area_profile} poiDensity={dbf.poi_density} />
      </Section>
    );
  }
  return <ProfilKelurahanSection facts={report.facts} accent={accent} />;
}

// Ecosystem for the map: the shared Google+OSM categories, plus (Iklan) the
// advertiser groups as categories of their own so prospects show on the map.
function mapEcosystem(report, withAdvertisers) {
  const eco = [...(report.ecosystem || [])];
  if (withAdvertisers) {
    (report.advertisers || []).forEach((g) => {
      if (g.count > 0 && g.places?.length) eco.push({ category: g.label, count: g.count, places: g.places, source: "advertiser" });
    });
  }
  return eco;
}

export function BisnisReport({ report, onRefresh, share, printMode = false }) {
  const km = radiusToKm(report?.radius);
  const center = { lat: report?.facts?.lat, lng: report?.facts?.lng };
  const ai = report.ai || {};
  const ranking = report.ranking || [];
  const max = Math.max(1, ...ranking.map((r) => r.opportunity || 0));
  const region = report.facts?.region || {};
  const rk = radiusInt(report.radius);
  const cat = report.facts?.catchment?.[rk] || report.facts?.catchment?.[3] || {};
  const r0 = ranking[0];
  const inp = report.inputs || {};
  const chip = [inp.tipe_ruang, inp.luas_m2 ? `${inp.luas_m2} m²` : null, inp.modal && inp.modal !== "semua" ? `modal ${inp.modal}` : null].filter(Boolean).join(" · ");
  // New reports carry three candidate markets; older ones only `market` (#1).
  const markets = (report.markets && report.markets.length ? report.markets : (report.market ? [report.market] : []));
  const bacaanBy = {};
  (ai.pembacaan_pasar_kandidat || []).forEach((b) => { if (b?.bisnis) bacaanBy[String(b.bisnis).toLowerCase()] = b.bacaan; });
  const bacaanFor = (m, i) => (i === 0 ? ai.pembacaan_pasar : null) || bacaanBy[String(m.subcategory).toLowerCase()];
  const demo = report.db_facts?.demographics;
  const mapComps = markets[0]?.competitors || [];
  return (
    <div className="bg-slate-50 dark:bg-slate-900/30 p-3.5 sm:p-4 space-y-3.5 text-slate-700 dark:text-slate-200">
      <Hero from="#0a7d57" to="#12b886" eyebrow="Potensi Bisnis · Analisis Mendalam"
        title={[region.kelurahan, region.kecamatan].filter(Boolean).join(", ") || "Lokasi"}
        sub={`${region.kabkot || ""} · radius ${rk} km`} chip={chip || null}
        ringValue={r0?.opportunity} pickEyebrow="Peluang teratas" pickName={r0?.subcategory} verdict={r0?.verdict && ({ "belum tergarap": "● Belum tergarap", seimbang: "● Seimbang", jenuh: "● Jenuh" }[r0.verdict])} />

      <StatRow tiles={[
        r0 && { label: "Peluang teratas", value: Math.round(r0.opportunity), sub: r0.subcategory, accent: scoreColor(r0.opportunity), icon: Target },
        cat.pop_total != null && { label: "Populasi", value: fmtN(cat.pop_total), sub: `radius ${rk} km`, icon: Users },
        report.facts?.buying_power_index != null && { label: "Daya beli", value: Math.round(report.facts.buying_power_index), sub: "/100 nasional", accent: "#0f9d6e", icon: Wallet },
        markets[0]?.count != null && { label: "Pesaing #1", value: `${markets[0].count}${markets[0].capped ? "+" : ""}`, sub: r0?.verdict, icon: Store },
      ]} />

      {ai.ringkasan && <Section title="Ringkasan" icon={Sparkles} ai><p className="text-[12.5px] leading-relaxed">{ai.ringkasan}</p><p className="text-[10px] text-slate-400 mt-2">✦ Narasi di laporan ini disusun AI dari data lapangan yang tampil di tiap bagian. Angka, peringkat, dan peta dihitung dari data, bukan ditulis AI.</p></Section>}

      {/* ── Peluang ── */}
      <GroupHeader ai label="Peluang" hint="Usaha apa yang paling berpotensi di titik ini" />
      <div className="grid lg:grid-cols-2 gap-3.5 items-start">
        {ranking.length > 2 && (
          <Section title="Peta peluang — fit × kejenuhan" icon={Target}>
            <Quadrant points={ranking.map((r) => ({ x: (r.fit || 0) / 100, y: r.saturation ?? 0, label: r.subcategory, color: scoreColor(r.opportunity) }))} />
            <p className="text-[10px] text-slate-400 mt-1 text-center">tiap titik = 1 bisnis · kanan-atas = fit tinggi &amp; belum jenuh</p>
          </Section>
        )}
        {(ai.top_picks || []).length > 0 && (
          <Section title="Rekomendasi teratas" icon={Sparkles} ai long>
            <div className="space-y-2.5">
              {ai.top_picks.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-800/40 p-3">
                  <div className="flex items-center gap-2"><span className="w-5 h-5 rounded-full text-white text-[11px] font-black flex items-center justify-center shrink-0" style={{ backgroundColor: ["#0f9d6e", "#16b886", "#2bd69b"][i] || "#5bc98f" }}>{i + 1}</span><p className="text-[13px] font-extrabold text-slate-800 dark:text-slate-100">{p.bisnis}</p>{p.kelayakan && <span className="ml-auto text-[10px] font-semibold text-slate-400">{p.kelayakan}</span>}</div>
                  {p.alasan && <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">{p.alasan}</p>}
                  <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px] text-slate-500">
                    {p.target_pelanggan && <span><b className="text-slate-600 dark:text-slate-300">Target:</b> {p.target_pelanggan}</span>}
                    {p.estimasi_modal && <span><b className="text-slate-600 dark:text-slate-300">Modal:</b> {p.estimasi_modal}</span>}
                    {p.potensi_pendapatan && <span><b className="text-slate-600 dark:text-slate-300">Omzet:</b> {p.potensi_pendapatan}</span>}
                    {p.jam_operasi_ideal && <span><b className="text-slate-600 dark:text-slate-300">Jam ideal:</b> {p.jam_operasi_ideal}</span>}
                    {p.sewa_bulanan && <span><b className="text-slate-600 dark:text-slate-300">Sewa:</b> Rp {fmtN(p.sewa_bulanan.rendah_idr)}–{fmtN(p.sewa_bulanan.tinggi_idr)}/bln ({p.sewa_bulanan.luas_m2[0]}–{p.sewa_bulanan.luas_m2[1]} m²)</span>}
                  </div>
                  {p.positioning && <p className="text-[10px] text-slate-400 mt-0.5">Positioning: {p.positioning}</p>}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
      <Section title="Peringkat peluang" icon={TrendingUp} long>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] tabular-nums">
            <thead><tr className="text-[9px] uppercase tracking-wide text-slate-400 text-right">
              <th className="text-left pb-1.5 font-bold">#</th><th className="text-left pb-1.5 font-bold">Usaha</th><th className="pb-1.5 font-bold">Peluang</th><th className="pb-1.5 font-bold">Kecocokan</th><th className="pb-1.5 font-bold">Pasokan</th><th className="pb-1.5 font-bold">per 10rb</th><th className="text-left pb-1.5 font-bold pl-3">Kejenuhan</th>
            </tr></thead>
            <tbody>
              {ranking.slice(0, 15).map((r, i) => (
                <tr key={r.subcategory} className="border-t border-slate-100 text-right">
                  <td className="text-left py-1.5 text-slate-300 font-bold">{i + 1}</td>
                  <td className="text-left py-1.5 font-semibold text-slate-700">{r.subcategory}<span className="block text-[9px] text-slate-400 font-normal">{r.category}</span></td>
                  <td className="py-1.5"><div className="inline-flex items-center gap-2 justify-end"><div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(r.opportunity / max) * 100}%`, backgroundColor: scoreColor(r.opportunity) }} /></div><span className="font-extrabold w-6" style={{ color: scoreColor(r.opportunity) }}>{Math.round(r.opportunity)}</span></div></td>
                  <td className="py-1.5 text-slate-600">{r.fit != null ? Math.round(r.fit) : "—"}</td>
                  <td className="py-1.5 text-slate-600">{r.supply ?? "—"}</td>
                  <td className="py-1.5 text-slate-600">{r.per10k ?? "—"}</td>
                  <td className="text-left py-1.5 pl-3"><VerdictBadge v={r.verdict} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">Peluang = kecocokan lokasi × (1 − penalti kejenuhan). Pasokan = usaha sejenis dalam 1,5 km (OSM); "per 10rb" = pasokan per 10.000 penduduk.</p>
      </Section>

      {/* ── Area ── */}
      <GroupHeader ai label="Area" hint="Siapa yang tinggal di sini, berapa biayanya, apa yang ada di sekitar" />
      {ai.karakter_area && <Section title="Karakter area" icon={MapPinned} ai><p className="text-[12px] leading-relaxed">{ai.karakter_area}</p></Section>}
      {((report.ecosystem || []).length > 0 || mapComps.some((c) => c.lat != null)) && (
        <Section title="Peta lokasi" icon={MapPinned}>
          <ReportMap lat={center.lat} lng={center.lng} competitors={mapComps} ecosystem={report.ecosystem || []} staticImage={printMode} />
        </Section>
      )}
      <div className="grid sm:grid-cols-2 gap-3.5 items-start">
        <div className="space-y-3.5">
          <AreaPropertiSection facts={report.facts} property={report.property} note={ai.area_properti} />
          <SewaSection rent={report.rent_estimate} picks={ai.top_picks} />
          <DayaBeliSection facts={report.facts} property={report.property} premium={report.premium_brands || []} />
          {ai.daya_beli && <Section title="Baca daya beli" icon={Wallet} ai><p className="text-[12px] leading-relaxed">{ai.daya_beli}</p></Section>}
        </div>
        <div className="space-y-3.5">
          {demo ? (
            <Section title={`Populasi dalam radius ${demo.radius}`} icon={Users}><PopulationByGroup demo={demo} /></Section>
          ) : <DemografiSection facts={report.facts} radius={report.radius} />}
          <PopulasiMultiRadius facts={report.facts} radius={report.radius} />
          <AreaBlock report={report} accent="#0f9d6e" />
        </div>
      </div>
      {(report.ecosystem || []).length > 0 && (
        <Section title="Ekosistem pendukung" icon={Building2} long>
          <Ecosystem ecosystem={report.ecosystem} radius={report.radius} printMode={printMode} />
        </Section>
      )}
      <EkosistemSection facts={report.facts} />
      {(ai.ekosistem || []).length > 0 && <Section title="Bacaan ekosistem" icon={Building2} ai><ul className="space-y-1 list-disc list-inside text-[12px] text-slate-600 dark:text-slate-300">{ai.ekosistem.map((e, i) => <li key={i} className="leading-relaxed">{e}</li>)}</ul></Section>}

      {/* ── Kompetisi ── */}
      <GroupHeader ai label="Kompetisi" hint="Pesaing nyata untuk kandidat teratas, dan bagaimana kinerja mereka" />
      {markets.map((m, i) => (
        <KandidatPasarSection key={m.subcategory || i} market={m} index={i} bacaan={bacaanFor(m, i)} center={center} radiusKm={km} printMode={printMode} />
      ))}
      {ai.kompetisi && <Section title="Kompetisi &amp; kejenuhan" icon={Store} ai><p className="text-[12px] leading-relaxed">{ai.kompetisi}</p></Section>}

      {/* ── Waktu ── */}
      {(ai.ritme_area || []).length > 0 && <GroupHeader ai label="Waktu" hint="Kapan area ini ramai" />}
      <RitmeSection items={ai.ritme_area} />

      {/* ── Rencana ── */}
      <GroupHeader ai label="Rencana" hint="Modal, posisi, risiko, dan langkah berikutnya" />
      {ai.modal_kelayakan && <Section title="Modal &amp; kelayakan" icon={Wallet} ai>
        <p className="text-[12px] leading-relaxed">{ai.modal_kelayakan}</p>
        {report.property?.land_price_typical_idr_m2 && <p className="text-[10px] text-slate-400 mt-1.5">Harga lahan tipikal: Rp {fmtN(report.property.land_price_typical_idr_m2)}/m²{report.property.confidence ? ` · keyakinan ${report.property.confidence}` : ""}</p>}
      </Section>}
      {ai.positioning_pasar && <Section title="Positioning pasar" icon={Target} ai><p className="text-[12px] leading-relaxed">{ai.positioning_pasar}</p></Section>}
      <div className="grid sm:grid-cols-2 gap-3.5">
        {(ai.risiko || []).length > 0 && <Section title="Risiko &amp; mitigasi" icon={AlertTriangle} ai accent="#e0a100"><ul className="space-y-1.5">{ai.risiko.map((r, i) => <li key={i} className="text-[11px] leading-relaxed"><b className="text-slate-700 dark:text-slate-200">{r.risiko}</b> <span className="text-slate-500">— {r.mitigasi}</span></li>)}</ul></Section>}
        {(ai.aksi || []).length > 0 && <Section title="Langkah selanjutnya" icon={CheckCircle2} ai><ol className="list-decimal list-inside space-y-1 text-[12px] text-slate-600 dark:text-slate-300">{ai.aksi.map((a, i) => <li key={i}>{a}</li>)}</ol></Section>}
      </div>

      {!printMode && <ReportFooter report={report} type="potensi_bisnis" onRefresh={onRefresh} share={share} />}
    </div>
  );
}

const IK_FACTOR_LABELS = { youth: "Usia muda", family: "Keluarga", elderly: "Lansia", female: "Perempuan", affluence: "Daya beli", office: "Perkantoran", campus: "Kampus", commuter: "Komuter/transit", mall: "Ritel/mal", reach: "Jangkauan",
  edu_poi: "Sekolah/kampus", health_poi: "Kesehatan", housing_poi: "Perumahan", auto_poi: "Otomotif", finance_poi: "Keuangan", retail_poi: "Ritel besar", food_poi: "Kuliner", travel_poi: "Hotel/wisata", beauty_poi: "Salon/gym" };
const IK_CORE_FACTORS = ["youth", "family", "elderly", "female", "affluence", "office", "campus", "commuter", "mall", "reach"];

// `audience` = "advertiser": the proposal edition — audience, reach and the one
// vertical it is aimed at; no ranking, no local prospects, no pricing, no pitch.
export function IklanReport({ report, onRefresh, share, printMode = false, audience = "internal", vertical = null }) {
  const ai = report.ai || {};
  const verticals = report.verticals || [];
  const max = Math.max(1, ...verticals.map((v) => v.score || 0));
  const jang = ai.jangkauan || {};
  const region = report.facts?.region || {};
  const rk = radiusInt(report.radius);
  const cat = report.facts?.catchment?.[rk] || report.facts?.catchment?.[3] || {};
  const v0v = verticals[0];
  const inp = report.inputs || {};
  const chip = [inp.format, inp.posisi, inp.jam_tayang].filter(Boolean).join(" · ");
  const center = { lat: report.facts?.lat, lng: report.facts?.lng };
  const proposal = audience === "advertiser";
  const target = proposal && vertical ? verticals.find((v) => v.id === vertical) : null;
  const targetWhy = target ? (ai.top_verticals || []).find((t) => String(t.vertikal).toLowerCase() === target.name.toLowerCase()) : null;
  const demo = report.db_facts?.demographics;
  const coreFactors = Object.entries(report.factors || {}).filter(([k]) => IK_CORE_FACTORS.includes(k));
  return (
    <div className="bg-slate-50 dark:bg-slate-900/30 p-3.5 sm:p-4 space-y-3.5 text-slate-700 dark:text-slate-200">
      <Hero from="#a21caf" to="#d946ef" eyebrow={proposal ? "Proposal Media Luar Ruang" : "Potensi Iklan · Analisis Mendalam"}
        title={[region.kelurahan, region.kecamatan].filter(Boolean).join(", ") || "Lokasi"}
        sub={`${region.kabkot || ""} · radius ${rk} km`} chip={chip || null}
        ringValue={proposal ? (target?.score ?? null) : v0v?.score} ringLabel="cocok"
        pickEyebrow={proposal ? "Kecocokan untuk" : "Iklan teratas"} pickName={proposal ? target?.name : v0v?.name} />

      <StatRow tiles={[
        { label: "Impresi/hari", value: fmtN(report.impressions_daily_est), accent: "#c026d3", icon: Megaphone },
        cat.pop_total != null && { label: "Jangkauan", value: fmtN(cat.pop_total), sub: `radius ${rk} km`, icon: Users },
        report.facts?.buying_power_index != null && { label: "Daya beli", value: Math.round(report.facts.buying_power_index), sub: "/100", accent: "#0f9d6e", icon: Wallet },
        !proposal && v0v && { label: "Iklan teratas", value: v0v.score, sub: v0v.name, accent: scoreColor(v0v.score), icon: Target },
        !proposal && (report.advertisers || []).length > 0 && { label: "Pengiklan lokal", value: report.advertisers.filter((g) => g.count > 0).length, sub: "kelompok di sekitar", accent: "#c026d3", icon: Store },
      ]} />

      {ai.ringkasan && !proposal && <Section title="Ringkasan" icon={Sparkles} accent="#c026d3" ai><p className="text-[12.5px] leading-relaxed">{ai.ringkasan}</p><p className="text-[10px] text-slate-400 mt-2">✦ Narasi di laporan ini disusun AI dari data lapangan yang tampil di tiap bagian. Angka, peringkat, dan peta dihitung dari data, bukan ditulis AI.</p></Section>}

      {/* ── Audiens ── */}
      <GroupHeader ai label="Audiens" hint="Siapa yang melintas, berapa banyak, dan kapan" />
      <div className="grid lg:grid-cols-2 gap-3.5 items-start">
        <Section title="Jangkauan & lalu lintas" icon={Route} accent="#c026d3">
          <p className="text-3xl font-extrabold text-slate-800 dark:text-slate-100 tabular-nums leading-none">{Math.round(report.impressions_daily_est || 0).toLocaleString("id-ID")}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">estimasi impresi / hari{jang.catatan_impresi ? ` · ${jang.catatan_impresi}` : ""}</p>
          {report.reach_frequency && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-fuchsia-50 dark:bg-fuchsia-500/10 px-3 py-2.5">
                <p className="text-[18px] font-extrabold text-fuchsia-700 dark:text-fuchsia-300 tabular-nums leading-none">{fmtN(report.reach_frequency.reach_weekly)}</p>
                <p className="text-[10px] text-slate-500 mt-1">orang <b>berbeda</b> / minggu</p>
              </div>
              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5">
                <p className="text-[18px] font-extrabold text-slate-700 dark:text-slate-200 tabular-nums leading-none">{report.reach_frequency.frequency_weekly}×</p>
                <p className="text-[10px] text-slate-500 mt-1">frekuensi / orang / minggu</p>
              </div>
            </div>
          )}
          {report.share_of_voice && !proposal && (
            <p className="text-[11px] mt-2.5">
              <b className="text-slate-700 dark:text-slate-200">Share of voice:</b> ~{report.share_of_voice.share_pct}%
              {report.share_of_voice.competing_screens > 0 ? ` — ${report.share_of_voice.competing_screens} layar lain di ruas ini` : " — tidak ada layar lain di ruas ini"}
              <span className="text-slate-400"> (keterangan pemilik aset, bukan pengukuran)</span>
            </p>
          )}
          {jang.jam_ramai && <p className="text-[12px] leading-relaxed mt-2"><b className="text-slate-700 dark:text-slate-200">Jam ramai:</b> {jang.jam_ramai}</p>}
          {(jang.arah || report.approach?.summary) && <p className="text-[12px] leading-relaxed mt-1"><b className="text-slate-700 dark:text-slate-200">Arah datang:</b> {jang.arah || report.approach.summary}</p>}
          {(jang.dayparting || []).length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {jang.dayparting.map((d, i) => (
                <div key={i} className="rounded-xl border border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-800/40 px-2.5 py-2">
                  <div className="flex items-center gap-1 text-fuchsia-500 mb-0.5"><Clock className="w-3 h-3" /><span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">{d.waktu}</span></div>
                  <p className="text-[10px] text-slate-500 leading-snug">{d.aktivitas}</p>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section title="Profil audiens" icon={Users} accent="#c026d3">
          {coreFactors.length > 0 && (
            <RadarChart items={coreFactors.map(([k, v]) => ({ label: IK_FACTOR_LABELS[k] || k, value: v || 0 }))} />
          )}
          {ai.audiens && <p className="text-[12px] leading-relaxed mt-2">{ai.audiens}</p>}
        </Section>
      </div>
      <div className="grid sm:grid-cols-2 gap-3.5 items-start">
        <div className="space-y-3.5">
          {demo ? (
            <Section title={`Populasi dalam radius ${demo.radius}`} icon={Users} accent="#c026d3"><PopulationByGroup demo={demo} /></Section>
          ) : <DemografiSection facts={report.facts} radius={report.radius} />}
          <PopulasiMultiRadius facts={report.facts} radius={report.radius} accent="#c026d3" />
        </div>
        <div className="space-y-3.5">
          <DayaBeliSection facts={report.facts} property={report.property} premium={report.premium_brands || []} />
          <AreaPropertiSection facts={report.facts} property={report.property} note={ai.area_properti} accent="#c026d3" />
          <AreaBlock report={report} accent="#c026d3" />
        </div>
      </div>

      {/* ── Pengiklan ── */}
      {proposal ? (
        target && (
          <>
            <GroupHeader ai label="Kecocokan" hint={`Kenapa layar ini tepat untuk ${target.name}`} />
            <Section title={`Kenapa cocok — ${target.name}`} icon={Sparkles} accent="#c026d3" ai>
              <p className="text-[12px] leading-relaxed">{targetWhy?.alasan || `Audiens di titik ini cocok untuk ${target.name}: ${(target.why || []).join(", ")}.`}</p>
              {targetWhy?.contoh_pengiklan && <p className="text-[10px] text-slate-400 mt-1">Contoh: {targetWhy.contoh_pengiklan}</p>}
            </Section>
          </>
        )
      ) : (
        <>
          <GroupHeader ai label="Pengiklan" hint="Siapa yang akan membeli slot di layar ini" />
          <PengiklanLokalSection advertisers={report.advertisers} ai={ai.pengiklan_lokal} />
          <Section title="Kategori iklan nasional yang cocok" icon={Megaphone} accent="#c026d3" long>
            <p className="text-[10px] text-slate-400 mb-2">Skor dari profil audiens sensus + POI lokal (sekolah, dealer, perumahan, dst. ikut menaikkan kategori terkait).</p>
            <div className="space-y-1.5">{verticals.slice(0, 10).map((v, i) => (
              <div key={v.id} className="flex items-center gap-3"><span className="w-4 text-right text-[10px] font-bold text-slate-300">{i + 1}</span><div className="min-w-0 flex-1"><p className="text-[12px] font-semibold text-slate-700 dark:text-slate-200 truncate">{v.name}{(v.why || []).length > 0 && <span className="text-[10px] text-slate-400 font-normal"> · {v.why.join(", ")}</span>}</p><div className="mt-0.5 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(v.score / max) * 100}%`, backgroundColor: scoreColor(v.score) }} /></div></div><span className="text-[13px] font-extrabold tabular-nums" style={{ color: scoreColor(v.score) }}>{v.score}</span></div>
            ))}</div>
          </Section>
          {(ai.top_verticals || []).length > 0 && <Section title="Kenapa cocok" icon={Sparkles} accent="#c026d3" ai long><div className="space-y-2">{ai.top_verticals.map((t, i) => <div key={i}><p className="text-[12px] font-bold text-slate-700 dark:text-slate-200">{t.vertikal}</p>{t.alasan && <p className="text-[11px] text-slate-500 leading-relaxed">{t.alasan}</p>}{t.contoh_pengiklan && <p className="text-[10px] text-slate-400">Contoh: {t.contoh_pengiklan}</p>}</div>)}</div></Section>}
          <KalenderSection items={ai.kalender} />
        </>
      )}

      {/* ── Lokasi ── */}
      <GroupHeader label="Lokasi" hint="Apa yang ada di sekitar layar ini" />
      {(report.ecosystem || []).length > 0 && (
        <Section title="Peta & anchor sekitar" icon={MapPinned} accent="#c026d3">
          <ReportMap lat={center.lat} lng={center.lng} competitors={[]} ecosystem={mapEcosystem(report, !proposal)} staticImage={printMode} />
        </Section>
      )}
      {(report.ecosystem || []).length > 0 && (
        <Section title="Ekosistem sekitar" icon={Building2} accent="#c026d3" long>
          <Ecosystem ecosystem={report.ecosystem} radius={report.radius} printMode={printMode} />
        </Section>
      )}
      <EkosistemSection facts={report.facts} />

      {/* ── Komersial ── */}
      <GroupHeader ai label="Komersial" hint={proposal ? "Format dan kreatif" : "Format, harga slot, dan cara menjualnya"} />
      {ai.format_rekomendasi && <Section title="Format & kreatif" icon={Sparkles} accent="#c026d3" ai><p className="text-[12px] leading-relaxed">{ai.format_rekomendasi}</p></Section>}
      {!proposal && <NilaiSlotSection slot={report.slot_value} note={ai.nilai_slot} />}
      {!proposal && (ai.pitch || []).length > 0 && <Section title="Sudut penawaran ke pengiklan" icon={Target} accent="#c026d3" ai><ul className="space-y-1.5">{ai.pitch.map((p, i) => <li key={i} className="text-[11px] leading-relaxed"><b className="text-slate-700 dark:text-slate-200">{p.vertikal}</b> <span className="text-slate-500">— {p.sudut_jual}</span></li>)}</ul></Section>}
      {ai.catatan && <Section title="Catatan" icon={AlertTriangle} accent="#e0a100"><p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">{ai.catatan}</p></Section>}

      {!printMode && <ReportFooter report={report} type="potensi_iklan" onRefresh={onRefresh} share={share} />}
    </div>
  );
}
